import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { listPublishedPackages } from '../../api/packages';
import { buildPackageMenus, fromApiPackage, packages as builtInPackages, themesOf } from './data';

const PackagesCtx = createContext(null);

/** Published packages from the API, followed by the built-in catalogue. */
export function PackagesProvider({ children }) {
  const [apiPackages, setApiPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async (signal) => {
    try {
      const list = await listPublishedPackages(signal);
      setApiPackages(list.map(fromApiPackage));
    } catch (error) {
      // Without the API the site still shows the built-in packages.
      if (error.name === 'AbortError') return;
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal);
    return () => controller.abort();
  }, [reload]);

  const value = useMemo(() => {
    const all = [...apiPackages, ...builtInPackages];
    return {
      packages: all,
      loading,
      reload: () => reload(),
      getPackage: (id) => all.find((p) => p.id === id),
      themes: themesOf(all),
      menus: buildPackageMenus(all),
    };
  }, [apiPackages, loading, reload]);

  return <PackagesCtx.Provider value={value}>{children}</PackagesCtx.Provider>;
}

export function usePackages() {
  const value = useContext(PackagesCtx);
  if (!value) throw new Error('usePackages must be used inside PackagesProvider.');
  return value;
}
